import { Injectable } from "@angular/core";
import { EventBusService, communityPostShare, communityPostShareMailto, environment } from "@mypxplat/xplat/core";
import { TranslateService } from "@ngx-translate/core";
import { ClipboardService } from "ngx-clipboard";
import { firstValueFrom } from "rxjs";
import { take } from "rxjs/operators";

/** What came of a share, so the button can say so — "copied" flashes a check on it. */
export type CommunityShareOutcome = "copied" | "shared" | "emailed" | null;

/**
 * Sharing a community post from the web: the app's action chooser with Copy Link first (what
 * most people want on a desktop), the browser's own share sheet where it has one (Safari,
 * Edge, Chrome on Windows and on phones), and Email.
 *
 * The mobile app opens the system share sheet directly instead — see ns-myfender `shareLink`.
 * Both build the link with `communityPostShare`, so the two land on the same URL.
 */
@Injectable({ providedIn: "root" })
export class CommunityShareService {
  constructor(
    private _eventBus: EventBusService,
    private _clipboard: ClipboardService,
    private _translate: TranslateService
  ) {}

  /** Community posts only — beta posts are bug reports on unreleased products and are never shared. */
  async share(post: any, opts: { communityStringId?: string } = {}): Promise<CommunityShareOutcome> {
    const share = communityPostShare(post, { fenderUrl: environment.fenderUrl, ...opts });
    const copy = this._translate.instant("com.copy_link");
    const native = this._canShareNatively(share.url) ? this._translate.instant("com.share_via") : null;
    const email = this._translate.instant("com.share_email");

    this._eventBus.emit(this._eventBus.types.showActionChooser, {
      title: this._translate.instant("com.share_post"),
      actions: [{ label: copy, icon: "link" }, native && { label: native, icon: "arrow-up-from-bracket" }, { label: email, icon: "envelope" }].filter(Boolean),
    });
    const choice = await firstValueFrom(this._eventBus.observe(this._eventBus.types.actionChosen).pipe(take(1)));

    if (choice === copy) {
      this._clipboard.copy(share.url);
      return "copied";
    }
    if (native && choice === native) {
      try {
        await navigator.share({ title: share.title, url: share.url });
        return "shared";
      } catch {
        // Dismissing the sheet rejects with AbortError — nothing to report.
        return null;
      }
    }
    if (choice === email) {
      window.location.href = communityPostShareMailto(share);
      return "emailed";
    }
    return null;
  }

  private _canShareNatively(url: string): boolean {
    if (typeof navigator === "undefined" || typeof navigator.share !== "function") return false;
    return typeof navigator.canShare !== "function" || navigator.canShare({ url });
  }
}
